import React, { useContext, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  Modal,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import PostAddIcon from "@mui/icons-material/PostAdd";
import IProduto from "../Shared/Interfaces/Produto";
import { formatToBRL } from "../../Utils/utils";
import { CadastroService } from "../../Services/CadastrosService";
import { TipoCadastro } from "../Shared/Enums/TipoCadastro";
import DataItem from "../Shared/Interfaces/DataItem";
import { EstoqueService } from "../../Services/EstoqueService";
import { AppProvider } from "../../ContextApi/AppProvider";
import colors from "../../theme/colors";

const estoqueService = new EstoqueService();
const cadastroService = new CadastroService();

const initialProduto: IProduto = {
  id: 0,
  descricao: "",
  medida: "",
  tipoPneu: "",
  tipoProduto: "",
  marca: "",
  lote: 0,
  codigo: 0,
  quantidade: 0,
  valor: 0,
};

const styleModal = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95vw", sm: 920 },
  maxWidth: "96vw",
  maxHeight: "90vh",
  overflowY: "auto" as const,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: { xs: 2, sm: 3 },
  borderRadius: 1,
};

export interface ModalCadastrarProdutoProps {
  open: boolean;
  onClose: () => void;
  /** Chamado após cadastro com sucesso (produto retornado ou montado a partir do formulário). */
  onProdutoCriado?: (produto: IProduto) => void;
}

const ModalCadastrarProduto: React.FC<ModalCadastrarProdutoProps> = ({
  open,
  onClose,
  onProdutoCriado,
}) => {
  const contextApi = useContext(AppProvider);
  const [form, setForm] = useState<IProduto>({ ...initialProduto });
  const [medidas, setMedidas] = useState<DataItem[]>([]);
  const [marcas, setMarcas] = useState<DataItem[]>([]);
  const [tiposDePneus, setTiposDePneus] = useState<DataItem[]>([]);
  const [tiposDeProdutos, setTiposDeProdutos] = useState<DataItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...initialProduto });
    const load = async () => {
      try {
        const [m, tp, tprod, mar] = await Promise.all([
          cadastroService.getAll(
            `/api/Cadastros/Buscar?tipoCadastro=${TipoCadastro.Medidas}`
          ),
          cadastroService.getAll(
            `/api/Cadastros/Buscar?tipoCadastro=${TipoCadastro.TiposDePneus}`
          ),
          cadastroService.getAll(
            `/api/Cadastros/Buscar?tipoCadastro=${TipoCadastro.TiposDeProdutos}`
          ),
          cadastroService.getAll(
            `/api/Cadastros/Buscar?tipoCadastro=${TipoCadastro.Marcas}`
          ),
        ]);
        setMedidas(m.data || []);
        setTiposDePneus(tp.data || []);
        setTiposDeProdutos(tprod.data || []);
        setMarcas(mar.data || []);
      } catch (e) {
        contextApi.showMessage(
          "Erro ao carregar listas de cadastro.",
          true,
          "error"
        );
      }
    };
    load();
  }, [open]);

  const handleInput = (fieldName: keyof IProduto, value: string) => {
    setForm((prev) => {
      if (fieldName === "valor") {
        const unformattedValue = value.replace(/\D/g, "");
        const numericValue = Number(unformattedValue) / 100;
        return { ...prev, valor: numericValue };
      }
      if (fieldName === "quantidade") {
        return { ...prev, quantidade: parseInt(value, 10) || 0 };
      }
      return { ...prev, [fieldName]: value };
    });
  };

  const salvar = async () => {
    const valorNum = parseFloat(
      form.valor
        .toString()
        .replace(/[^\d,.-]/g, "")
        .replace(",", ".")
    );
    const payload: IProduto = {
      ...form,
      valor: Number.isNaN(valorNum) ? 0 : valorNum,
    };

    if (
      !payload.descricao?.trim() ||
      payload.quantidade === 0 ||
      payload.valor === 0
    ) {
      contextApi.showMessage(
        "Preencha descrição, quantidade e valor (como na gestão de estoque).",
        true,
        "warning"
      );
      return;
    }

    setLoading(true);
    try {
      const response = await estoqueService.postItem(
        "/api/Produtos/Adicionar",
        payload
      );
      if (response.status === 201) {
        contextApi.showMessage(
          "Produto cadastrado no estoque com sucesso!",
          true,
          "success"
        );
        const data = response.data as IProduto | undefined;
        const criado: IProduto =
          data && typeof data.id === "number"
            ? { ...payload, ...data, valor: data.valor ?? payload.valor }
            : { ...payload, id: (data as { id?: number })?.id ?? 0 };
        onProdutoCriado?.(criado);
        onClose();
      } else {
        contextApi.showMessage(
          "Erro ao incluir o produto. Tente novamente.",
          true,
          "error"
        );
      }
    } catch (error: any) {
      const msg =
        error?.response?.data != null
          ? typeof error.response.data === "string"
            ? error.response.data
            : JSON.stringify(error.response.data)
          : "Erro ao cadastrar produto.";
      contextApi.showMessage(msg, true, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={() => !loading && onClose()}>
      <Box sx={styleModal}>
        <Typography
          variant="h6"
          sx={{ mb: 0.5, color: colors.bluePrimary, display: "flex", alignItems: "center", gap: 1 }}
        >
          <PostAddIcon fontSize="small" />
          Cadastrar produto no estoque
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Mesmos campos da gestão de itens (Gerenciar estoque). O produto passa a
          aparecer na lista desta compra após salvar.
        </Typography>

        <Paper elevation={0} sx={{ p: 0 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={5}>
              <TextField
                fullWidth
                label="Descrição"
                size="small"
                value={form.descricao}
                onChange={(e) => handleInput("descricao", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                options={medidas}
                getOptionLabel={(o) => o.descricao}
                size="small"
                value={
                  medidas.find((o) => o.descricao === form.medida) || null
                }
                onChange={(_, v) => handleInput("medida", v?.descricao || "")}
                renderInput={(params) => (
                  <TextField {...params} label="Medida" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                options={tiposDePneus}
                getOptionLabel={(o) => o.descricao}
                size="small"
                value={
                  tiposDePneus.find((o) => o.descricao === form.tipoPneu) ||
                  null
                }
                onChange={(_, v) =>
                  handleInput("tipoPneu", v?.descricao || "")
                }
                renderInput={(params) => (
                  <TextField {...params} label="Tipo de Pneu" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                options={tiposDeProdutos}
                getOptionLabel={(o) => o.descricao}
                size="small"
                value={
                  tiposDeProdutos.find(
                    (o) => o.descricao === form.tipoProduto
                  ) || null
                }
                onChange={(_, v) =>
                  handleInput("tipoProduto", v?.descricao || "")
                }
                renderInput={(params) => (
                  <TextField {...params} label="Tipo de Produto" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                options={marcas}
                getOptionLabel={(o) => o.descricao}
                size="small"
                value={
                  marcas.find((o) => o.descricao === form.marca) || null
                }
                onChange={(_, v) => handleInput("marca", v?.descricao || "")}
                renderInput={(params) => (
                  <TextField {...params} label="Marca" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Quantidade"
                type="number"
                size="small"
                value={form.quantidade || ""}
                onChange={(e) => handleInput("quantidade", e.target.value)}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Valor"
                size="small"
                value={formatToBRL(Number(form.valor) || 0)}
                onChange={(e) => handleInput("valor", e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={salvar}
            disabled={loading}
            startIcon={<PostAddIcon />}
          >
            Salvar no estoque
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ModalCadastrarProduto;
